import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, Newspaper, GraduationCap, Stethoscope, BookMarked, Pill, ArrowLeft, FileText } from "lucide-react";
import { useAuth } from "../AuthContext";
import { API_URL } from "../config";

const KIND_CONFIG = {
  post:      { path: "articles", label: "Статья", icon: Newspaper },
  case:      { path: "cases", label: "Случай", icon: Stethoscope },
  lecture:   { path: "lectures", label: "Материал", icon: GraduationCap },
  // У guideline нет собственной страницы на портале — path не используется,
  // ссылка идёт через s.url (внешняя, на реестр Минздрава), см. рендер ниже.
  guideline: { path: null, label: "Клин. рекомендация", icon: BookMarked },
};

function renderAnswer(text, sources) {
  // Заменяем [1] [2] и т.д. на кликабельные значки-ссылки на источник
  const parts = text.split(/(\[\d+\])/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[(\d+)\]$/);
    if (!m) return <span key={i}>{part}</span>;
    const n = Number(m[1]);
    const src = sources.find(s => s._n === n);
    const cfg = src && KIND_CONFIG[src.kind];
    if (!src || !cfg) return <span key={i}>{part}</span>;
    const linkStyle = { color: "#e5005b", fontWeight: 700, textDecoration: "none" };
    if (cfg.path === null) {
      return (
        <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" title={src.title} style={linkStyle}>
          [{n}]
        </a>
      );
    }
    return (
      <Link key={i} to={`/${cfg.path}/${src.id}`} title={src.title} style={linkStyle}>
        [{n}]
      </Link>
    );
  });
}

function AiSearchMode({ onLoginRequest }) {
  const { isLoggedIn, token } = useAuth();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = e => { e.preventDefault(); run(query); };

  const run = async text => {
    if (!isLoggedIn) { onLoginRequest && onLoginRequest(); return; }
    const q = (text || "").trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/ai-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ query: q }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "ИИ-поиск временно недоступен");
      const sources = (data.sources || []).map((s, i) => ({ ...s, _n: s.n ?? i + 1 }));
      setResult({ answer: data.answer, sources });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Запуск из шапки: /ai-search?q=вопрос — подставляем и сразу отвечаем (один раз на вопрос)
  const [params] = useSearchParams();
  const urlQ = params.get("q");
  const ranFor = useRef(null);
  useEffect(() => {
    if (!urlQ || ranFor.current === urlQ) return;
    setQuery(urlQ);
    if (!isLoggedIn) { onLoginRequest && onLoginRequest(); return; }
    ranFor.current = urlQ;
    run(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQ, isLoggedIn]);

  return (
    <section style={{ maxWidth: 720 }}>
      <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Sparkles size={20} strokeWidth={2.2} color="#e5005b" /> Умный поиск по порталу
      </h2>
      <p style={{ color: "#888", marginTop: -8, marginBottom: 16, fontSize: "0.92rem" }}>
        Задайте вопрос своими словами — ассистент найдёт материалы портала и ответит на их основе.
      </p>

      <form onSubmit={submit} style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Например: как вести пациента с фибрилляцией предсердий?"
          style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid #c4ceda", fontSize: "0.95rem", outline: "none" }}
        />
        <button type="submit" disabled={loading}
          style={{ background: "#e5005b", color: "#fff", border: "none", borderRadius: 8, padding: "0 20px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          {loading ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />}
          Спросить
        </button>
      </form>

      {!isLoggedIn && (
        <p style={{ color: "#888", fontStyle: "italic" }}>Войдите, чтобы задать вопрос ИИ-ассистенту.</p>
      )}

      {error && (
        <div className="card" style={{ background: "#fee2e2", color: "#b91c1c", border: "1.5px solid #fecaca" }}>
          {error}
        </div>
      )}

      {result && (
        <div className="card" style={{ background: "#fff1f6", border: "1.5px solid #ffc2d9" }}>
          <div style={{ color: "#9c0040", fontSize: "0.98rem", lineHeight: 1.6 }}>
            {renderAnswer(result.answer, result.sources)}
          </div>
          <div style={{ marginTop: 10, fontSize: "0.78rem", color: "var(--gray-600)" }}>
            Ответ сформирован автоматически (ИИ) на основе материалов портала и клинических рекомендаций. Проверяйте по источникам.
          </div>
          {result.sources.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #ffc2d9" }}>
              <div style={{ fontSize: "0.82rem", color: "#e5005b", fontWeight: 700, marginBottom: 6 }}>Источники:</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {result.sources.map(s => {
                  const cfg = KIND_CONFIG[s.kind];
                  if (!cfg) return null;
                  const Icon = cfg.icon;
                  const rowStyle = { display: "flex", alignItems: "center", gap: 6, color: "#007c84", textDecoration: "none", fontSize: "0.9rem" };
                  if (cfg.path === null) {
                    return (
                      <a key={`${s.kind}_${s.id}`} href={s.url} target="_blank" rel="noopener noreferrer" style={rowStyle}>
                        <span style={{ fontWeight: 700 }}>[{s._n}]</span>
                        <Icon size={13} strokeWidth={2.2} /> {s.title}
                        <span style={{ color: "#999", fontSize: "0.78rem" }}>({cfg.label}, реестр Минздрава)</span>
                      </a>
                    );
                  }
                  return (
                    <Link key={`${s.kind}_${s.id}`} to={`/${cfg.path}/${s.id}`} style={rowStyle}>
                      <span style={{ fontWeight: 700 }}>[{s._n}]</span>
                      <Icon size={13} strokeWidth={2.2} /> {s.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function DrugLookup({ onLoginRequest }) {
  const { isLoggedIn, token } = useAuth();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);   // выбранный DrugMatch
  const [instruction, setInstruction] = useState(null);
  const [instrLoading, setInstrLoading] = useState(false);

  const search = async e => {
    e.preventDefault();
    if (!isLoggedIn) { onLoginRequest && onLoginRequest(); return; }
    const q = query.trim();
    if (q.length < 3) { setError("Введите минимум 3 символа"); return; }
    setLoading(true);
    setError("");
    setMatches(null);
    setSelected(null);
    setInstruction(null);
    try {
      const r = await fetch(`${API_URL}/drug-lookup/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "ГРЛС временно недоступен");
      setMatches(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openDrug = async drug => {
    setSelected(drug);
    setInstruction(null);
    setInstrLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/drug-lookup/${drug.routing_guid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось загрузить инструкцию");
      setInstruction(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setInstrLoading(false);
    }
  };

  if (selected) {
    return (
      <section style={{ maxWidth: 720 }}>
        <button onClick={() => setSelected(null)}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#e5005b", fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 14, fontSize: "0.92rem" }}>
          <ArrowLeft size={16} /> К результатам поиска
        </button>
        <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Pill size={20} strokeWidth={2.2} color="#e5005b" /> {selected.trade_name}
        </h2>
        <p style={{ color: "#888", marginTop: -8, marginBottom: 16, fontSize: "0.9rem" }}>
          {selected.mnn} · {selected.dosage_form} · {selected.manufacturer}
          {instruction?.reg_number && <> · рег. № {instruction.reg_number}</>}
        </p>

        {instrLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#888" }}>
            <Loader2 size={16} className="spin" /> Загружаем инструкцию из ГРЛС…
          </div>
        )}

        {error && (
          <div className="card" style={{ background: "#fee2e2", color: "#b91c1c", border: "1.5px solid #fecaca" }}>
            {error}
          </div>
        )}

        {instruction && !instruction.text && (
          <div className="card" style={{ background: "#fff1f6", color: "#9c0040", border: "1.5px solid #ffc2d9" }}>
            {instruction.note || "Текст инструкции недоступен."}
            {instruction.pdf_url && (
              <div style={{ marginTop: 8 }}>
                <a href={instruction.pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: "#9c0040", fontWeight: 700 }}>
                  Открыть на сайте ГРЛС →
                </a>
              </div>
            )}
          </div>
        )}

        {instruction?.text && (
          <div className="card" style={{ background: "#fff1f6", border: "1.5px solid #ffc2d9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", color: "#e5005b", fontWeight: 700, marginBottom: 10 }}>
              <FileText size={14} strokeWidth={2.2} /> Официальная инструкция (ГРЛС)
            </div>
            <div style={{ color: "#007c84", fontSize: "0.92rem", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
              {instruction.text}
            </div>
            {instruction.pdf_url && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #ffc2d9" }}>
                <a href={instruction.pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: "#e5005b", fontWeight: 700, fontSize: "0.88rem" }}>
                  Полный текст PDF на сайте ГРЛС →
                </a>
              </div>
            )}
          </div>
        )}
      </section>
    );
  }

  return (
    <section style={{ maxWidth: 720 }}>
      <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Pill size={20} strokeWidth={2.2} color="#e5005b" /> Справочник лекарств
      </h2>
      <p style={{ color: "#888", marginTop: -8, marginBottom: 16, fontSize: "0.92rem" }}>
        Официальные инструкции по применению из Государственного реестра лекарственных средств.
      </p>

      <form onSubmit={search} style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="МНН или торговое название, например: метформин"
          style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid #c4ceda", fontSize: "0.95rem", outline: "none" }}
        />
        <button type="submit" disabled={loading}
          style={{ background: "#e5005b", color: "#fff", border: "none", borderRadius: 8, padding: "0 20px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          {loading ? <Loader2 size={16} className="spin" /> : <Pill size={16} />}
          Найти
        </button>
      </form>

      {!isLoggedIn && (
        <p style={{ color: "#888", fontStyle: "italic" }}>Войдите, чтобы искать препараты.</p>
      )}

      {error && (
        <div className="card" style={{ background: "#fee2e2", color: "#b91c1c", border: "1.5px solid #fecaca" }}>
          {error}
        </div>
      )}

      {matches && matches.length === 0 && (
        <p style={{ color: "#888" }}>Ничего не найдено. Проверьте написание или попробуйте МНН.</p>
      )}

      {matches && matches.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {matches.map(m => (
            <button key={m.routing_guid} onClick={() => openDrug(m)}
              style={{ textAlign: "left", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "12px 14px", cursor: "pointer" }}>
              <div style={{ fontWeight: 700, color: "#1f2937" }}>{m.trade_name}</div>
              <div style={{ color: "#6b7280", fontSize: "0.86rem", marginTop: 2 }}>
                {m.mnn} · {m.dosage_form}
              </div>
              <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginTop: 2 }}>
                {m.manufacturer} · рег. № {m.reg_number}
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AiSearch({ onLoginRequest }) {
  const [mode, setMode] = useState("ai");   // "ai" | "drugs"

  const tabStyle = active => ({
    padding: "8px 16px",
    borderRadius: 8,
    border: "1.5px solid " + (active ? "#e5005b" : "#e5e7eb"),
    background: active ? "#e5005b" : "#fff",
    color: active ? "#fff" : "#4b5563",
    fontWeight: 600,
    fontSize: "0.88rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  });

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 18, maxWidth: 720 }}>
        <button style={tabStyle(mode === "ai")} onClick={() => setMode("ai")}>
          <Sparkles size={15} strokeWidth={2.2} /> Умный поиск
        </button>
        <button style={tabStyle(mode === "drugs")} onClick={() => setMode("drugs")}>
          <Pill size={15} strokeWidth={2.2} /> Справочник лекарств
        </button>
      </div>
      {mode === "ai" ? <AiSearchMode onLoginRequest={onLoginRequest} /> : <DrugLookup onLoginRequest={onLoginRequest} />}
    </div>
  );
}
